import { useState, useEffect, useRef } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Alert, AlertDescription } from './ui/alert';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { RadioGroup, RadioGroupItem } from './ui/radio-group';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { AlertCircle, CheckCircle, Loader2, Building2, Upload, FileText, X, Sparkles } from 'lucide-react';
import { projectId } from '../utils/supabase/info';
import { getAuthState } from '../utils/authState';
import { hasAnyRole } from '../utils/roleUtils';
import { TemplateSelector } from './templates/TemplateSelector';
import type { TemplateType } from '../types';
import { SalesNotesAssist, type BdmExtraction } from './SalesNotesAssist';
import { InvitationEmailEditor, STANDARD, type InvitationDraft } from './InvitationEmailEditor';
import { platformTemplateFor } from '../utils/clientIntake';

// Reserved company names that cannot be used
const RESERVED_NAMES = [
  'pathkit', 'admin', 'system', 'test', 'demo', 'staging',
  'api', 'app', 'www', 'mail', 'support', 'help', 'billing',
  'scopify', 'internal', 'root', 'null', 'undefined',
];

interface CompanySetupProps {
  accessToken: string;
  onCompanyCreated?: () => void;
  currentUser?: any;
}

interface User {
  id: string;
  email: string;
  name: string;
  companyId?: string;
  role?: string;
}

interface ConsultingFirm {
  id: string;
  name: string;
  subdomain?: string;
}

export function CompanySetup({ accessToken, onCompanyCreated, currentUser }: CompanySetupProps) {
  const [companyName, setCompanyName] = useState('');
  const [adminEmail, setAdminEmail] = useState('');
  const [adminName, setAdminName] = useState('');
  const [adminMode, setAdminMode] = useState<'new' | 'existing'>('new');
  const [selectedUserId, setSelectedUserId] = useState('');
  const [selectedTemplate, setSelectedTemplate] = useState<TemplateType>('atlassian');
  // Once the consultant picks a template, an extraction's recommendation no longer changes it
  const [templateTouched, setTemplateTouched] = useState(false);
  const [selectedFirmId, setSelectedFirmId] = useState('');
  const [consultingFirms, setConsultingFirms] = useState<ConsultingFirm[]>([]);
  const [firmsLoading, setFirmsLoading] = useState(false);
  const [users, setUsers] = useState<User[]>([]);
  const [usersLoading, setUsersLoading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  // What did not happen during creation (config items, firm list, email), from the server.
  const [warnings, setWarnings] = useState<string[]>([]);
  const [nameCheckLoading, setNameCheckLoading] = useState(false);
  const [nameAvailable, setNameAvailable] = useState<boolean | null>(null);
  const [nameError, setNameError] = useState('');

  // SOW Upload & Sales Notes state
  const [sowFile, setSowFile] = useState<File | null>(null);
  const [salesNotes, setSalesNotes] = useState('');
  // What Quick Extract or the notes chat pulled out of the sales notes (RESTORE_PLAN R3.4)
  const [bdmExtracted, setBdmExtracted] = useState<BdmExtraction | null>(null);
  // The new admin's invitation email: a firm template or the standard welcome, edited here
  const [invitation, setInvitation] = useState<InvitationDraft>({ templateId: STANDARD, body: '' });
  const [activationStatus, setActivationStatus] = useState<string | null>(null);
  const sowInputRef = useRef<HTMLInputElement>(null);

  // Debounce company name validation
  useEffect(() => {
    if (!companyName || companyName.length < 2) {
      setNameAvailable(null);
      setNameError('');
      return;
    }

    const timeoutId = setTimeout(async () => {
      await checkCompanyNameAvailability(companyName);
    }, 500);

    return () => clearTimeout(timeoutId);
  }, [companyName]);

  const checkCompanyNameAvailability = async (name: string) => {
    setNameCheckLoading(true);
    setNameError('');
    try {
      const normalizedName = name.toLowerCase().trim();
      
      // First check if name is reserved
      if (RESERVED_NAMES.includes(normalizedName)) {
        setNameAvailable(false);
        setNameError('This name is reserved by the system and cannot be used');
        setNameCheckLoading(false);
        return;
      }

      // Use dedicated backend endpoint that checks both Postgres and KV
      const response = await fetch(
        `https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd/admin/check-company-name?name=${encodeURIComponent(name.trim())}`,
        {
          headers: {
            'Authorization': `Bearer ${accessToken}`,
          },
        }
      );

      if (response.ok) {
        const data = await response.json();
        if (data.available) {
          setNameAvailable(true);
          setNameError('');
        } else {
          setNameAvailable(false);
          setNameError(data.existingName 
            ? `A company named "${data.existingName}" already exists`
            : 'This company name is already in use');
        }
      }
    } catch (err) {
      console.error('Error checking company name:', err);
      // Don't block submission if check fails
      setNameAvailable(null);
    } finally {
      setNameCheckLoading(false);
    }
  };

  // Load users when component mounts or when switching to existing user mode
  useEffect(() => {
    if (adminMode === 'existing' && users.length === 0) {
      loadUsers();
    }
  }, [adminMode]);

  // Load consulting firms on mount (for System Admins only)
  useEffect(() => {
    // Show firm selector ONLY when actively viewing as System Admin
    // CF Admins (even if they have isAdmin=true from being a switched system admin)
    // should auto-bind to their firm, not see the selector
    const activeRole = currentUser?.activeRole || currentUser?.role;
    const isViewingAsSystemAdmin = activeRole === 'systemAdmin' || activeRole === 'system_admin';
    
    console.log('[CF_ADMIN_CONTEXT] create modal mounted', {
      activeRole,
      isViewingAsSystemAdmin,
      firmId: currentUser?.firmId || 'UNDEFINED',
      consultingFirmId: currentUser?.consultingFirmId || 'UNDEFINED',
      isAdmin: currentUser?.isAdmin,
    });
    
    if (isViewingAsSystemAdmin && consultingFirms.length === 0) {
      loadConsultingFirms();
    }
    
    // Auto-set firm for non-system-admin firm roles (cfAdmin, consultant, etc.)
    if (!isViewingAsSystemAdmin) {
      const userFirmId = currentUser?.firmId || currentUser?.consultingFirmId;
      console.log('[CF_ADMIN_CONTEXT] active firmId:', userFirmId || 'UNDEFINED');
      if (userFirmId && !selectedFirmId) {
        setSelectedFirmId(userFirmId);
        console.log('[CF_ADMIN_CONTEXT] Auto-bound firm for', activeRole, ':', userFirmId);
      } else if (!userFirmId) {
        // Last-resort: check localStorage for firm context saved during role switch
        const storedUser = localStorage.getItem('pathkit_user');
        if (storedUser) {
          try {
            const parsed = JSON.parse(storedUser);
            const lsFirmId = parsed.firmId || parsed.consultingFirmId;
            if (lsFirmId) {
              setSelectedFirmId(lsFirmId);
              console.log('[CF_ADMIN_CONTEXT] Recovered firmId from localStorage:', lsFirmId);
            } else {
              console.warn('[CF_ADMIN_CONTEXT] ⚠️ No firmId in localStorage user either');
            }
          } catch (e) {
            console.warn('[CF_ADMIN_CONTEXT] Failed to parse localStorage user');
          }
        }
      }
    }
  }, [currentUser]);

  const loadConsultingFirms = async () => {
    setFirmsLoading(true);
    try {
      const response = await fetch(
        `https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd/admin/consulting-firms`,
        {
          headers: {
            'Authorization': `Bearer ${accessToken}`,
          },
        }
      );

      if (response.ok) {
        const data = await response.json();
        setConsultingFirms(data.firms || []);
      } else {
        console.error('Failed to load consulting firms');
      }
    } catch (err) {
      console.error('Error loading consulting firms:', err);
    } finally {
      setFirmsLoading(false);
    }
  };

  const loadUsers = async () => {
    setUsersLoading(true);
    try {
      const response = await fetch(
        `https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd/admin/all-users`,
        {
          headers: {
            'Authorization': `Bearer ${accessToken}`,
          },
        }
      );

      if (response.ok) {
        const data = await response.json();
        // Filter to show only users without a company or who are not already company admins
        const availableUsers = data.users.filter((u: User) => 
          !u.companyId || u.role === 'user' || u.role === 'companyUser'
        );
        setUsers(availableUsers);
      } else {
        console.error('Failed to load users');
      }
    } catch (err) {
      console.error('Error loading users:', err);
    } finally {
      setUsersLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setWarnings([]);
    setSuccess(false);

    try {
      // Check if company name is already in use or reserved
      if (nameAvailable === false) {
        setError(nameError || 'This company name is not available. Please choose a different name.');
        setLoading(false);
        return;
      }

      const requestBody: any = {
        companyName,
        platformTemplate: selectedTemplate,
        bdmNotes: salesNotes.trim() || undefined,
        bdmExtractedData: bdmExtracted || undefined,
      };
      // The invitation goes to a new admin only; an existing user is not emailed
      if (adminMode === 'new') {
        requestBody.emailTemplate = invitation.templateId;
        requestBody.emailBody = invitation.body.trim() || undefined;
      }
      
      // Determine if we need to send consulting firm context
      // System Admin viewing as systemAdmin: must select from dropdown
      // CF Admin (or system admin viewing as cfAdmin): auto-bind from user context
      const activeRole = currentUser?.activeRole || currentUser?.role;
      const isViewingAsSystemAdmin = activeRole === 'systemAdmin' || activeRole === 'system_admin';
      
      if (isViewingAsSystemAdmin) {
        // System Admin must explicitly select a firm
        if (!selectedFirmId) {
          setError('Please select a consulting firm');
          setLoading(false);
          return;
        }
        requestBody.consultingFirmId = selectedFirmId;
      } else {
        // CF Admin / Consultant / BD Rep: auto-bind to their firm
        const userFirmId = selectedFirmId || currentUser?.firmId || currentUser?.consultingFirmId;
        if (userFirmId) {
          requestBody.consultingFirmId = userFirmId;
          console.log('[CF_ADMIN_CONTEXT] Auto-binding new company to firm:', userFirmId);
        } else {
          console.warn('[CF_ADMIN_CONTEXT] WARNING: No firm context available for', activeRole);
          setError('Missing consulting firm context. Please try switching back to System Admin and then to CF Admin again.');
          setLoading(false);
          return;
        }
      }

      if (adminMode === 'existing') {
        if (!selectedUserId) {
          setError('Please select a user');
          setLoading(false);
          return;
        }
        requestBody.existingUserId = selectedUserId;
      } else {
        requestBody.adminEmail = adminEmail;
        requestBody.adminName = adminName;
      }

      // Determine the correct endpoint based on user role
      // CF Admins, BD Reps, and Consultants use the consultant endpoint
      // Only System Admins viewing as systemAdmin use the admin endpoint
      const effectiveRole = currentUser?.activeRole || currentUser?.role;
      const isConsultingFirmUser = effectiveRole === 'consultant' || 
                                    effectiveRole === 'cfAdmin' ||
                                    effectiveRole === 'cf-admin' ||
                                    effectiveRole === 'bdRep' ||
                                    effectiveRole === 'bd-rep' ||
                                    effectiveRole === 'billingAdmin' ||
                                    effectiveRole === 'Engagement Lead' ||
                                    effectiveRole === 'Engineer';
      const endpoint = isConsultingFirmUser
        ? 'consultant/create-company'
        : 'admin/create-company';

      console.log('[CF_ADMIN_CONTEXT] submit payload consultingFirmId:', requestBody.consultingFirmId || 'MISSING');
      console.log('[CF_ADMIN_CONTEXT] submit endpoint:', endpoint);
      console.log('[CF_ADMIN_CONTEXT] submit effectiveRole:', effectiveRole);

      const response = await fetch(
        `https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd/${endpoint}`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${accessToken}`,
          },
          body: JSON.stringify(requestBody),
        }
      );

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to create company');
      }

      // --- Post-creation activation: Upload SOW if provided ---
      const engagementId = data.company?.engagementId;
      const createdCompanyId = data.companyId || data.company?.id;
      let sowUploadOk = false;
      let hlosGenerated = 0;

      if (sowFile && engagementId) {
        setActivationStatus('Uploading SOW and generating scope...');
        try {
          const formData = new FormData();
          formData.append('file', sowFile);

          const sowRes = await fetch(
            `https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd/engagements/${engagementId}/upload-sow`,
            {
              method: 'POST',
              headers: {
                'Authorization': `Bearer ${accessToken}`,
              },
              body: formData,
            }
          );

          if (sowRes.ok) {
            const sowData = await sowRes.json();
            sowUploadOk = true;
            hlosGenerated = sowData.hloCount || sowData.hlos?.length || 0;
            console.log(`[ACTIVATION] SOW uploaded, ${hlosGenerated} HLOs generated for engagement ${engagementId}`);
          } else {
            const sowErr = await sowRes.json().catch(() => ({}));
            console.error('[ACTIVATION] SOW upload failed:', sowErr);
          }
        } catch (sowError) {
          console.error('[ACTIVATION] SOW upload error:', sowError);
        }
      }

      setActivationStatus(null);
      setSuccess(true);
      
      // Build success message with activation status
      const messageParts: string[] = [];
      if (adminMode === 'existing') {
        messageParts.push('Company created. The selected user is now its Client Engagement Lead.');
      } else if (data.passwordResetSent) {
        messageParts.push(`Company created. A password reset email has been sent to ${adminEmail}. The new admin must check their email and set a password before they can log in.`);
      } else {
        messageParts.push(data.message || 'Company created.');
      }

      if (sowUploadOk) {
        messageParts.push(`SOW processed successfully${hlosGenerated > 0 ? ` — ${hlosGenerated} objectives extracted.` : '.'}`);
      } else if (sowFile) {
        messageParts.push('SOW upload encountered an issue — you can re-upload from the engagement view.');
      }

      if (salesNotes.trim()) {
        messageParts.push('Sales discovery notes saved — preliminary objectives will be generated.');
      }

      const createdItems = Number(data.provisioning?.configItems ?? 0);
      if (createdItems > 0) {
        messageParts.push(`${createdItems} configuration items created from the template.`);
      }
      setWarnings(Array.isArray(data.warnings) ? data.warnings.filter((w: unknown) => typeof w === 'string') : []);

      setSuccessMessage(messageParts.join(' '));
      setCompanyName('');
      setAdminEmail('');
      setAdminName('');
      setSelectedUserId('');
      setSelectedFirmId('');
      setAdminMode('new');
      setSowFile(null);
      setSalesNotes('');
      setBdmExtracted(null);
      setInvitation({ templateId: STANDARD, body: '' });
      setTemplateTouched(false);

      if (onCompanyCreated) {
        onCompanyCreated();
      }

    } catch (err: any) {
      console.error('Error creating company:', err);
      setError(err.message || 'Failed to create company');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      {error && (
        <Alert variant="destructive">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {success && (
        <Alert className="border-green-500 bg-green-50 dark:bg-green-950">
          <CheckCircle className="h-4 w-4 text-green-600 dark:text-green-400" />
          <AlertDescription className="text-green-800 dark:text-green-200">
            {successMessage}
          </AlertDescription>
        </Alert>
      )}

      {success && warnings.length > 0 && (
        <Alert className="border-amber-500 bg-amber-50 dark:bg-amber-950" data-testid="create-company-warnings">
          <AlertCircle className="h-4 w-4 text-amber-600 dark:text-amber-400" />
          <AlertDescription className="text-amber-900 dark:text-amber-100">
            <p className="mb-1">The client was created, but not everything happened:</p>
            <ul className="list-disc pl-5 space-y-1">
              {warnings.map((warning, index) => (
                <li key={index}>{warning}</li>
              ))}
            </ul>
          </AlertDescription>
        </Alert>
      )}

      <Card>
        <CardHeader>
          <CardTitle>Template Selection</CardTitle>
          <CardDescription>
            Choose a platform template for this company
          </CardDescription>
        </CardHeader>
        <CardContent>
          <TemplateSelector
            selectedTemplate={selectedTemplate}
            onSelect={(t) => { setSelectedTemplate(t); setTemplateTouched(true); }}
          />
        </CardContent>
      </Card>

      {/* System Admins must select a consulting firm */}
      {(() => {
        const activeRole = currentUser?.activeRole || currentUser?.role;
        const isViewingAsSystemAdmin = activeRole === 'systemAdmin' || activeRole === 'system_admin';
        return isViewingAsSystemAdmin;
      })() && (
        <Card>
          <CardHeader>
            <CardTitle>Consulting Firm</CardTitle>
            <CardDescription>
              Select which consulting firm this company belongs to
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              <Label htmlFor="consulting-firm">Consulting Firm *</Label>
              <Select value={selectedFirmId} onValueChange={setSelectedFirmId}>
                <SelectTrigger id="consulting-firm">
                  <SelectValue placeholder={firmsLoading ? "Loading firms..." : "Select a consulting firm"} />
                </SelectTrigger>
                <SelectContent>
                  {consultingFirms.map((firm) => (
                    <SelectItem key={firm.id} value={firm.id}>
                      {firm.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-sm text-gray-500">
                The company will be assigned to this consulting firm and visible to their consultants.
              </p>
            </div>
          </CardContent>
        </Card>
      )}

      <form onSubmit={handleSubmit} className="space-y-6">
        <Card>
          <CardHeader>
            <CardTitle>Company Details</CardTitle>
            <CardDescription>
              Enter the company name
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="company-name">Company Name *</Label>
              <div className="relative">
                <Input
                  id="company-name"
                  value={companyName}
                  onChange={(e) => setCompanyName(e.target.value)}
                  placeholder="Acme Corporation"
                  required
                />
                {nameCheckLoading && (
                  <div className="absolute right-3 top-1/2 -translate-y-1/2">
                    <div className="h-4 w-4 animate-spin rounded-full border-2 border-gray-300 border-t-gray-600" />
                  </div>
                )}
                {!nameCheckLoading && nameAvailable === true && (
                  <div className="absolute right-3 top-1/2 -translate-y-1/2 text-green-600">
                    <CheckCircle className="h-4 w-4" />
                  </div>
                )}
              </div>
              {nameError && (
                <p className="text-sm text-red-600">{nameError}</p>
              )}
              {nameAvailable === true && (
                <p className="text-sm text-green-600">This name is available</p>
              )}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Client Engagement Lead</CardTitle>
            <CardDescription>
              The client's lead for this engagement: a new person, or someone who already has an account
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <RadioGroup value={adminMode} onValueChange={(value: 'new' | 'existing') => setAdminMode(value)}>
              <div className="space-y-4">
                <div className="flex items-start space-x-3 border rounded-lg p-4">
                  <RadioGroupItem value="new" id="new-admin" />
                  <Label htmlFor="new-admin" className="font-normal cursor-pointer">
                    Create a new Client Engagement Lead
                  </Label>
                </div>
                
                {adminMode === 'new' && (
                  <div className="ml-7 space-y-4 border-l-2 pl-4">
                    <div className="space-y-2">
                      <Label htmlFor="admin-name">Admin Name *</Label>
                      <Input
                        id="admin-name"
                        value={adminName}
                        onChange={(e) => setAdminName(e.target.value)}
                        placeholder="John Doe"
                        required={adminMode === 'new'}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="admin-email">Admin Email *</Label>
                      <Input
                        id="admin-email"
                        type="email"
                        value={adminEmail}
                        onChange={(e) => setAdminEmail(e.target.value)}
                        placeholder="admin@company.com"
                        required={adminMode === 'new'}
                      />
                    </div>
                  </div>
                )}

                <div className="flex items-start space-x-3 border rounded-lg p-4">
                  <RadioGroupItem value="existing" id="existing-admin" />
                  <Label htmlFor="existing-admin" className="font-normal cursor-pointer">
                    Assign an existing user as the lead
                  </Label>
                </div>
                
                {adminMode === 'existing' && (
                  <div className="ml-7 space-y-4 border-l-2 pl-4">
                    <div className="space-y-2">
                      <Label htmlFor="existing-user">Select User *</Label>
                      <Select value={selectedUserId} onValueChange={setSelectedUserId}>
                        <SelectTrigger id="existing-user">
                          <SelectValue placeholder={usersLoading ? "Loading users..." : "Select a user"} />
                        </SelectTrigger>
                        <SelectContent>
                          {users.map((user) => (
                            <SelectItem key={user.id} value={user.id}>
                              {user.name} ({user.email})
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                )}
              </div>
            </RadioGroup>
          </CardContent>
        </Card>

        {/* Statement of Work (SOW) Upload */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <FileText className="h-5 w-5 text-indigo-600" />
              Statement of Work (SOW)
            </CardTitle>
            <CardDescription>
              Upload the signed SOW so PathKit can generate initial scope and implementation structure.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              <input
                ref={sowInputRef}
                type="file"
                accept=".pdf,.docx,.doc,.txt"
                className="hidden"
                onChange={(e) => {
                  const file = e.target.files?.[0] || null;
                  setSowFile(file);
                }}
              />

              {!sowFile ? (
                <button
                  type="button"
                  onClick={() => sowInputRef.current?.click()}
                  className="w-full flex flex-col items-center justify-center gap-2 border-2 border-dashed border-gray-300 rounded-lg p-6 hover:border-indigo-400 hover:bg-indigo-50/50 transition-colors cursor-pointer"
                >
                  <Upload className="h-8 w-8 text-gray-400" />
                  <span className="text-sm font-medium text-gray-700">Click to upload SOW</span>
                  <span className="text-xs text-gray-500">PDF, DOCX, DOC, or TXT (max 20MB)</span>
                </button>
              ) : (
                <div className="flex items-center gap-3 border rounded-lg p-3 bg-indigo-50/50">
                  <FileText className="h-5 w-5 text-indigo-600 shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-800 truncate">{sowFile.name}</p>
                    <p className="text-xs text-gray-500">{(sowFile.size / 1024).toFixed(1)} KB</p>
                  </div>
                  <button
                    type="button"
                    onClick={() => {
                      setSowFile(null);
                      if (sowInputRef.current) sowInputRef.current.value = '';
                    }}
                    className="p-1 rounded hover:bg-gray-200 transition-colors"
                  >
                    <X className="h-4 w-4 text-gray-500" />
                  </button>
                </div>
              )}

              <div className="flex items-start gap-2 text-xs text-gray-500">
                <Sparkles className="h-3.5 w-3.5 text-indigo-400 mt-0.5 shrink-0" />
                <span>PathKit will extract scope items, generate configuration items, and create an initial engagement structure from the SOW.</span>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Sales Discovery Notes */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Sparkles className="h-5 w-5 text-amber-500" />
              Sales Discovery Notes
            </CardTitle>
            <CardDescription>
              Paste notes gathered during pre-sales conversations. PathKit will use these to draft initial objectives for client review.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              <Textarea
                id="sales-notes"
                aria-label="Sales discovery notes"
                value={salesNotes}
                onChange={(e) => {
                  setSalesNotes(e.target.value);
                  // Insights belong to the notes they came from
                  if (bdmExtracted) setBdmExtracted(null);
                }}
                placeholder={"Example:\n- Client wants to consolidate 3 legacy ticketing systems into one\n- Key pain point: 48-hour average response time on P1 incidents\n- CEO mandated 99.9% SLA by Q3\n- Budget approved for full ITSM transformation"}
                rows={6}
                className="resize-y"
              />
              <div className="flex items-start gap-2 text-xs text-gray-500">
                <Sparkles className="h-3.5 w-3.5 text-amber-400 mt-0.5 shrink-0" />
                <span>These notes will generate preliminary High-Level Objectives (HLOs) that the Client Engagement Lead can review and refine during onboarding.</span>
              </div>
              <SalesNotesAssist
                accessToken={accessToken}
                notes={salesNotes}
                extracted={bdmExtracted}
                onExtracted={(data) => {
                  setBdmExtracted(data);
                  // The recommended platform picks the template unless the consultant already chose one
                  const recommended = platformTemplateFor(data?.recommendedPlatform);
                  if (recommended && !templateTouched) setSelectedTemplate(recommended as TemplateType);
                }}
              />
            </div>
          </CardContent>
        </Card>

        {/* Invitation email for a new admin (an existing user is not emailed) */}
        {adminMode === 'new' && (
          <Card>
            <CardContent className="pt-6">
              <InvitationEmailEditor
                accessToken={accessToken}
                value={invitation}
                onChange={setInvitation}
                preview={{
                  companyName: companyName || 'the company',
                  adminName: adminName || 'the new admin',
                  consultantName: currentUser?.name || currentUser?.email || 'you',
                  consultantEmail: currentUser?.email || '',
                  firmName: currentUser?.firmName || 'your firm',
                }}
              />
            </CardContent>
          </Card>
        )}

        {/* Activation status indicator */}
        {activationStatus && (
          <Alert className="border-indigo-300 bg-indigo-50">
            <Loader2 className="h-4 w-4 text-indigo-600 animate-spin" />
            <AlertDescription className="text-indigo-800">
              {activationStatus}
            </AlertDescription>
          </Alert>
        )}

        {/* What happens next (the old add-client form's explainer, matched to what the server does now) */}
        <div className="rounded-md border p-4" data-testid="what-happens-next">
          <h4>What happens next?</h4>
          <ul className="text-muted-foreground space-y-1 mt-2 list-disc pl-5">
            <li>The client and its first engagement are created, with configuration items from the template</li>
            <li>The client appears on your client list</li>
            {adminMode === 'new' ? (
              <>
                <li>The new admin receives the invitation email with a link to set their password (it works for 7 days)</li>
                <li>After signing in, a guided setup walks them through their objectives and inviting their team</li>
              </>
            ) : (
              <li>The selected person becomes the Client Engagement Lead and signs in with their existing account</li>
            )}
            {bdmExtracted?.objectives?.length ? <li>The objectives from the sales notes become the client's first goals to review</li> : null}
          </ul>
        </div>

        <div className="flex gap-3">
          <Button type="submit" disabled={loading || nameAvailable === false}>
            {loading ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                {activationStatus ? 'Processing...' : 'Creating...'}
              </>
            ) : 'Create Company'}
          </Button>
        </div>
      </form>
    </div>
  );
}